/**
 * O pinout gráfico — o desenho do componente.
 *
 * **Nada aqui é cacheado, de propósito.** É a decisão do Bruno e ela é do
 * trabalho, não da tecnologia: esta é a tela de quem está *editando*. A pessoa
 * mexe no pinout e olha o desenho para conferir o que mudou. Um desenho guardado
 * responderia rápido e mostraria o estado anterior — pior que demorar, porque a
 * conferência passaria e o erro ficaria.
 *
 * Por isso: sem `useQuery`, sem `staleTime`, sem `Cache-Control` do navegador.
 * Cada "Abrir" gera de novo. E gerar é lento — medido em 08/09/2026 contra o
 * projeto 976: **18 s** para o ECM (168 pinouts), 1,2 s para um de 10. Não é
 * cache frio: `PinoutGraficoService.Gerar` não consulta cache nenhum, monta e
 * desenha. As três medições seguidas deram o mesmo tempo.
 *
 * A tela precisa então fazer três coisas que uma tela rápida não precisa:
 * disparar só quando a pessoa pede, mostrar que está trabalhando, e deixar
 * desistir. É o que o `AbortController` daqui serve.
 */

import { api } from "./cliente"

/** As cinco opções do "Mais detalhes" do legado, com os padrões da API. */
export type OpcoesGrafico = {
  /** 0=código - descrição, 1=código, 2=descrição, 3=apelido, 4=vazio. */
  textoComponente: number
  /** 0=descrição, 1=apelido, 2=vazio. */
  textoConector: number
  /** 0=descrição+cor, 1=apelido+cor, 2=descrição, 3=apelido, 4=cor, 5=vazio. */
  textoCircuito: number
  /** Ordena os pinos como no diagrama em vez de por pino. */
  ordemDiagrama: boolean
  /** Colapsa circuitos de aterramento no símbolo de terra. */
  simplificarAterramento: boolean
}

export const OPCOES_PADRAO: OpcoesGrafico = {
  textoComponente: 3,
  textoConector: 2,
  textoCircuito: 4,
  ordemDiagrama: false,
  simplificarAterramento: true,
}

/** Os rótulos são os do legado — quem usa já os conhece por este nome. */
export const TEXTO_COMPONENTE = [
  "Código - Descrição", "Código", "Descrição", "Apelido", "Não exibir",
]
export const TEXTO_CONECTOR = ["Descrição", "Apelido", "Não exibir"]
export const TEXTO_CIRCUITO = [
  "Circuito Descrição e Cor", "Circuito Apelido e Cor", "Circuito Descrição",
  "Circuito Apelido", "Cor Descrição", "Não exibir",
]

export type PedidoGrafico = {
  projetoId: number
  componenteId: number
  modeloId: number
  /** Ids de pinout, quando o desenho é só das linhas marcadas na grade. */
  pinouts?: number[]
  opcoes: OpcoesGrafico
}

function parametros(p: PedidoGrafico): Record<string, string> {
  const q: Record<string, string> = {
    projetoId: String(p.projetoId),
    componenteId: String(p.componenteId),
    modeloId: String(p.modeloId),
    textoComponente: String(p.opcoes.textoComponente),
    textoConector: String(p.opcoes.textoConector),
    textoCircuito: String(p.opcoes.textoCircuito),
    ordemDiagrama: String(p.opcoes.ordemDiagrama),
    simplificarAterramento: String(p.opcoes.simplificarAterramento),
  }
  if (p.pinouts && p.pinouts.length > 0) q.pinouts = p.pinouts.join(",")
  return q
}

export type DesenhoPronto = {
  svg: string
  /** Quanto demorou, em segundos. A tela mostra: 18 s sem explicação parece travamento. */
  segundos: number
  bytes: number
  /**
   * O tamanho que o desenho declara para si mesmo. O motor calcula isso a partir
   * do número de pinos, e varia muito: 546x304 num componente de 10 pinouts,
   * 786x5344 no ECM de 168. É o tamanho em que o desenho foi pensado, então é
   * nele que a tela abre — esticar para a largura da janela engorda tudo sem
   * mostrar mais nada.
   */
  largura: number
  altura: number
}

/**
 * Gera o desenho. Devolve o SVG como texto.
 *
 * `Cache-Control: no-cache` no pedido porque o navegador também guarda GET, e
 * aqui guardar é o defeito, não a otimização.
 */
export async function gerarSvg(p: PedidoGrafico, sinal?: AbortSignal): Promise<DesenhoPronto> {
  const comecou = performance.now()
  const r = await api.get<string>("api/pinouts/grafico", {
    params: parametros(p),
    responseType: "text",
    headers: { "Cache-Control": "no-cache" },
    signal: sinal,
  })
  const svg = String(r.data)
  const medida = /width='(\d+(?:\.\d+)?)px'\s+height='(\d+(?:\.\d+)?)px'/.exec(svg)
  return {
    svg,
    segundos: (performance.now() - comecou) / 1000,
    bytes: new Blob([svg]).size,
    largura: medida ? Number(medida[1]) : 0,
    altura: medida ? Number(medida[2]) : 0,
  }
}

/** PNG ou EPS do mesmo desenho. O EPS é o que vai para publicação. */
export async function baixarImagem(
  p: PedidoGrafico, formato: "png" | "eps", nome: string,
): Promise<void> {
  const r = await api.get<Blob>("api/pinouts/grafico/imagem", {
    params: { ...parametros(p), formato },
    responseType: "blob",
    headers: { "Cache-Control": "no-cache" },
  })
  salvar(r.data, `${nome}.${formato}`)
}

/** Baixa o SVG que já está na tela, sem gerar de novo. */
export function baixarSvg(svg: string, nome: string): void {
  salvar(new Blob([svg], { type: "image/svg+xml" }), `${nome}.svg`)
}

function salvar(dado: Blob, nome: string): void {
  const url = URL.createObjectURL(dado)
  const a = document.createElement("a")
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Sem o revoke o blob fica na memória até a aba fechar, e um SVG destes tem
  // 140 kB — trinta desenhos numa manhã já pesam.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Nome de arquivo previsível: projeto, componente e modelo. */
export function nomeDoArquivo(projeto: number, componente: string, modelo: string): string {
  const limpo = (s: string) => s.trim().replace(/[^\w\-.]+/g, "-").replace(/-+/g, "-").slice(0, 40)
  return `pinout-${projeto}-${limpo(componente)}-${limpo(modelo)}`
}


// ------------------------------------------------------- o desenho interno

/**
 * O mesmo desenho, do lado de dentro do componente.
 *
 * Contrato mais curto que o de fora: não há opção de texto nenhuma, porque não
 * há componente, conector nem circuito externos para nomear. O que há é o
 * componente, o modelo (que aplica as restrições) e, opcionalmente, um
 * componente interno para isolar.
 *
 * **Sem cache, pela mesma razão do externo** — quem abre isto está conferindo o
 * que acabou de lançar.
 */
export type PedidoGraficoInterno = {
  componenteId: number
  modeloId: number
  /** 0 = todos os componentes internos. */
  componenteInternoId?: number
}

function paramsInternos(p: PedidoGraficoInterno): Record<string, string> {
  return {
    componenteId: String(p.componenteId),
    modeloId: String(p.modeloId),
    componenteInternoId: String(p.componenteInternoId ?? 0),
  }
}

export async function gerarSvgInterno(
  p: PedidoGraficoInterno, sinal?: AbortSignal,
): Promise<DesenhoPronto> {
  const comecou = performance.now()
  const r = await api.get<string>("api/pinouts-internos/grafico", {
    params: { ...paramsInternos(p), formato: "svg" },
    responseType: "text",
    headers: { "Cache-Control": "no-cache" },
    signal: sinal,
  })
  const svg = String(r.data)
  const medida = /width='(\d+(?:\.\d+)?)px'\s+height='(\d+(?:\.\d+)?)px'/.exec(svg)
  return {
    svg,
    segundos: (performance.now() - comecou) / 1000,
    bytes: new Blob([svg]).size,
    largura: medida ? Number(medida[1]) : 0,
    altura: medida ? Number(medida[2]) : 0,
  }
}

export async function baixarImagemInterna(
  p: PedidoGraficoInterno, formato: "png" | "eps", nome: string,
): Promise<void> {
  const r = await api.get<Blob>("api/pinouts-internos/grafico", {
    params: { ...paramsInternos(p), formato },
    responseType: "blob",
    headers: { "Cache-Control": "no-cache" },
  })
  salvar(r.data, `${nome}.${formato}`)
}
